import { ResponsivePie } from "@nivo/pie";
import { Button } from "@/components/ui/button";
import { EditIcon, HistoryIcon } from "lucide-react";
import { formatNumberToDisplay } from "@/lib/api/common";
import { get_commodity_color, get_commodity_border_color } from "./common";

interface CustomerProfileCardsProps {
    commodity_top_level_items: any[];
    onEditClick: (commodityType: string) => void;
    onHistoryClick: (commodityType: string) => void;
    onCommodityClick: (commodityDisplayName: string) => void;
}

interface UnifiedPieChartProps {
    data: any[];
    emptyLabel: string;
    formatNumber: (num: number) => string;
}

const UnifiedPieChart = ({ data, emptyLabel, formatNumber }: UnifiedPieChartProps) => {
    const hasData = data && data.length > 0 && data.some(item => item.value > 0);
    
    const pieData = hasData 
        ? data.filter(d => d.value > 0)
        : [{ 
            id: "empty", 
            label: emptyLabel, 
            value: 100, 
            percent: 100, 
            color: "#9ca3af" 
        }];

    return (
        <div className="w-24 h-24">
            <ResponsivePie
                data={pieData}
                margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                innerRadius={0.3}
                padAngle={2}
                cornerRadius={2}
                activeOuterRadiusOffset={2}
                colors={(d) => d.data.color}
                borderWidth={0}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                isInteractive={true}
                tooltip={({ datum }) => (
                    <div className="bg-white p-2 shadow-lg rounded border text-xs">
                        <div className="font-semibold">{datum.label}</div>
                        <div>
                            {hasData 
                                ? `${formatNumber(datum.value)} (${formatNumberToDisplay(datum.data.percent, { suffix: "%" })})`
                                : "No data available"
                            }
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

export function CustomerProfileCards({
    commodity_top_level_items,
    onEditClick,
    onHistoryClick,
    onCommodityClick,
}: CustomerProfileCardsProps) {
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    return (
        <div className="flex flex-col 2xl:grid gap-2 2xl:grid-cols-2 5xl:grid-cols-3">
            {commodity_top_level_items.map((item: any) => {
                const totalProduction = item.projected_production;
                const baseFuturesPieData: any[] = item.futuresBreakdown; // Only show segments with data
                const baseBasisPieData: any[] = item.physicalBreakdown; // Only show segments with data

                // Create immutable arrays with "Other" category if needed
                const futuresPieData = baseFuturesPieData.filter(item => !["open_offer_id", "double_up_id"].includes(item.id || ""));

                const basisPieData = baseBasisPieData;
                const nonDoubleUpOpenOrderData = baseFuturesPieData.find(item => item.id === "open_offer_id");
                const doubleUpData = baseFuturesPieData.find(item => item.id === "double_up_id");

                return (
                    <div
                        key={`customer-risk-${item.customer_id || 'default'}-${item.commodity_type}`}
                        className={`relative p-4 rounded-lg border-2 ${get_commodity_border_color(item.commodity_type)} bg-white shadow-sm`}
                    >
                        {/* Commodity Header */}
                        <div className="flex items-center gap-3 mb-3 justify-between">
                            <span className="flex items-center">
                                <div className={`w-4 h-4 rounded-full ${get_commodity_color(item.commodity_type)}`}></div>
                                <h3 className="text-lg font-bold">
                                    <Button 
                                        className="text-lg font-bold underline decoration-solid decoration-sky-500 hover:decoration-sky-600" 
                                        variant="ghost" 
                                        onClick={() => onCommodityClick(item.commodity_display_name)}
                                    >
                                        {item.commodity_display_name}
                                    </Button>
                                </h3>
                            </span>

                            <span className="flex gap-2 items-center">
                                <Button 
                                    className="" 
                                    variant="ghost" 
                                    onClick={() => onEditClick(item.commodity_type)}
                                >
                                    <EditIcon className="h-4 w-4" />
                                </Button>
                                <Button 
                                    className="" 
                                    variant="outline" 
                                    onClick={() => onHistoryClick(item.commodity_type)}
                                >
                                    <HistoryIcon className="h-4 w-4" />
                                </Button>
                            </span>
                        </div>

                        <div className="flex flex-col md:grid md:grid-cols-1 gap-4">
                            {/* Production Summary */}
                            <div className="grid gap-4 mb-4 text-sm grid grid-cols-5">
                                <div className="text-center">
                                    <div className="text-gray-500">Percent Hedged</div>
                                    <div className="font-semibold text-gray-800">{formatNumberToDisplay(item.percent_hedged, { suffix: "%" })}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500">Acreage</div>
                                    <div className="font-semibold text-gray-800">{formatNumber(item.acreage)}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500">Yield</div>
                                    <div className="font-semibold text-gray-800">{formatNumberToDisplay(item.total_yield)}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500">Projected</div>
                                    <div className="font-semibold text-gray-800">{formatNumber(item.projected_production)}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500">Actual</div>
                                    <div className="font-semibold text-gray-800">{formatNumber(item.actual_production)}</div>
                                </div>
                            </div>

<div className="flex flex-col items-start justify-start gap-4 lg:grid lg:grid-cols-2">
                            <div className="flex flex-row items-start gap-4">
                                {/* Risk Breakdown with Pie Chart */}
                                <div className="flex items-center flex-row">
                                    <div className="flex-shrink-0">
                                        <UnifiedPieChart 
                                            data={futuresPieData} 
                                            emptyLabel="No Futures" 
                                            formatNumber={formatNumber}
                                        />
                                    </div>
                                </div>

                                {/* Breakdown Legend */}
                                <div className="flex-1">
                                    <div className="gap-1 text-xs grid grid-cols-2 justify-start">
                                        {futuresPieData.map((segment) => (
                                            <div key={segment.id} className="flex justify-start items-start gap-2">
                                                <div
                                                    className="w-3 h-3 rounded"
                                                    style={{ backgroundColor: segment.color }}
                                                ></div>
                                                <div className="flex-1">
                                                    <div className="font-medium">{segment.label} <span className="text-xs text-muted-foreground">({formatNumberToDisplay(segment.percent, { suffix: "%" })})</span></div>
                                                    <div className="text-gray-500">{formatNumber(segment.value)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row justify-start gap-4">
                                {/* Risk Breakdown with Pie Chart */}
                                <div className="flex justify-startflex-row">
                                    <div className="flex-shrink-0">
                                        <UnifiedPieChart 
                                            data={basisPieData} 
                                            emptyLabel="No Physical" 
                                            formatNumber={formatNumber}
                                        />
                                    </div>
                                </div>

                                {/* Breakdown Legend */}
                                <div className="flex-1">
                                    <div className="gap-1 text-xs grid grid-cols-2 justify-start items-start">
                                        {basisPieData.map((segment) => (
                                            <div key={segment.id} className="flex justify-start items-start gap-2">
                                                <div
                                                    className="w-3 h-3 rounded"
                                                    style={{ backgroundColor: segment.color }}
                                                ></div>
                                                <div className="flex-1">
                                                    <div className="font-medium">{segment.label} <span className="text-xs text-muted-foreground">({formatNumberToDisplay(segment.percent, { suffix: "%" })})</span></div>
                                                    <div className="text-gray-500">{formatNumber(segment.value)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            </div>
                            
                        </div>

                        {/* Overall Production Status */}
                        <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="grid grid-cols-5 gap-4 text-xs">
                                <div>
                                    <div className="text-gray-500">Production Sold</div>
                                    <div className="font-semibold text-green-700">{formatNumber(item.total_risk_counts.production_sold)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Production Unsold</div>
                                    <div className="font-semibold text-red-700">{formatNumber(item.total_risk_counts.production_unsold)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Double Up</div>
                                    <div className="font-semibold text-red-700">{formatNumberToDisplay(doubleUpData.value)}  ({formatNumberToDisplay(doubleUpData.percent)}%)</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Open Orders</div>
                                    <div className="font-semibold text-red-700">{formatNumberToDisplay(nonDoubleUpOpenOrderData.value)} ({formatNumberToDisplay(nonDoubleUpOpenOrderData.percent)}%)</div>
                                </div>
                                    <div>
                                        <div className="text-gray-500">Reown</div>
                                        <div className="font-semibold text-pink-700">{formatNumber(item.reown_total_quantity)}</div>
                                    </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
